import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];
const EMPTY_LINE = { account_id: "", debit: "", credit: "", narration: "" };

export default function PostJournal() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const { companies, selectedCompanyId } = useCompany() || {};
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!selectedCompanyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [entriesRes, accountsRes] = await Promise.all([
        api.get(`/gl/journal-entries?company_id=${selectedCompanyId}`),
        api.get("/gl/accounts"),
      ]);
      setEntries(entriesRes.data);
      setAccounts(accountsRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [selectedCompanyId]);

  const accountName = (id) => { const a = accounts.find((x) => x.id === id); return a ? `${a.code} — ${a.name}` : id; };

  return (
    <div className="space-y-6" data-testid="post-journal-page">
      <PageHeader
        title="Post Journal"
        description="Manual double-entry journal vouchers. Debits must equal credits to post."
        action={canManage && selectedCompanyId && <CreateJournalDialog companyId={selectedCompanyId} accounts={accounts} onCreated={load} />}
      />
      {!selectedCompanyId ? (
        <EmptyState title="Select a company from the top bar first" testId="post-journal-no-company" />
      ) : !loading && entries.length === 0 ? (
        <EmptyState title="No journal entries yet" testId="journal-entries-empty-state" />
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <div key={e.id} className="rounded-xl border border-border bg-card p-4" data-testid={`journal-entry-card-${e.id}`}>
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono text-xs text-muted-foreground">{e.reference || e.source_type}</span>
                  <span className="ml-2 text-foreground">{e.narration || "—"}</span>
                </div>
                <span className="text-xs text-muted-foreground">{e.entry_date}</span>
              </div>
              <Table className="mt-2">
                <TableHeader><TableRow><TableHead>Account</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {e.lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell className="text-muted-foreground">{accountName(line.account_id)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{Number(line.debit) > 0 ? line.debit : ""}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{Number(line.credit) > 0 ? line.credit : ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateJournalDialog({ companyId, accounts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ entry_date: "", narration: "", reference: "" });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const updateLine = (idx, field, value) => setLines(lines.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  const addLine = () => setLines([...lines, { ...EMPTY_LINE }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const totalDebit = lines.reduce((sum, l) => sum + (Number(l.debit) || 0), 0);
  const totalCredit = lines.reduce((sum, l) => sum + (Number(l.credit) || 0), 0);
  const balanced = totalDebit === totalCredit && totalDebit > 0;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/gl/journal-entries", {
        company_id: companyId, ...form,
        lines: lines.map((l) => ({ ...l, debit: l.debit || "0", credit: l.credit || "0" })),
      });
      toast.success("Journal entry posted");
      setForm({ entry_date: "", narration: "", reference: "" });
      setLines([{ ...EMPTY_LINE }, { ...EMPTY_LINE }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-journal-button"><Plus className="h-4 w-4" /> New Journal Entry</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>New Journal Entry</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-journal-form">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Date</Label>
              <Input required type="date" data-testid="journal-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" />
            </div>
            <div className="col-span-2">
              <Label>Narration</Label>
              <Input data-testid="journal-narration-input" value={form.narration} onChange={set("narration")} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label>Lines</Label>
            <div className="mt-1.5 space-y-2">
              {lines.map((line, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`journal-line-row-${idx}`}>
                  <Select value={line.account_id} onValueChange={(v) => updateLine(idx, "account_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`journal-line-account-select-${idx}`}><SelectValue placeholder="Account" /></SelectTrigger>
                    <SelectContent>{accounts.filter((a) => !a.is_group).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="0.01" placeholder="Debit" className="w-28" value={line.debit} onChange={(e) => updateLine(idx, "debit", e.target.value)} data-testid={`journal-line-debit-input-${idx}`} />
                  <Input type="number" step="0.01" placeholder="Credit" className="w-28" value={line.credit} onChange={(e) => updateLine(idx, "credit", e.target.value)} data-testid={`journal-line-credit-input-${idx}`} />
                  {lines.length > 2 && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(idx)} data-testid={`remove-journal-line-button-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-rose-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addLine} data-testid="add-journal-line-button">
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="journal-balance-indicator">
            <span className="text-muted-foreground">Debit: <span className="font-mono text-foreground">{totalDebit.toFixed(2)}</span> · Credit: <span className="font-mono text-foreground">{totalCredit.toFixed(2)}</span></span>
            <span className={balanced ? "font-semibold text-emerald-500" : "font-semibold text-rose-400"}>{balanced ? "Balanced" : "Not balanced"}</span>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || !balanced} data-testid="create-journal-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Post entry
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
